import { useEffect, useState } from 'react';
import { getAuthHeaders } from '@/hooks/useAuth';
import { getStoredAffiliateRef } from '@/lib/marketing';

// The yearly plan with Hey Joe credit. The credit steps down and then stays (Joe,
// Oct 16 2026): $50 through Mon Oct 19 (first 100), $35 through Sat Oct 31, then $25
// with every new year of Plus, permanently. Everything renders from /api/offer/oct26
// (bonusCents = today's amount, closesAt = when today's amount ends, null for the
// standing $25), so the copy changes itself at each step.
//   kind: 'signed_out' | 'new' | 'monthly' | 'yearly' | 'charter' | 'team' | 'none'
export type OctOffer = {
  open: boolean;
  /** Before the window opens (the page says when, rather than "ended"). */
  upcoming?: boolean;
  opensAt: string;
  /** When today's credit amount ends; null once it is the standing $25. */
  closesAt: string | null;
  /** The amount after closesAt, for "after Monday it's $35" lines. */
  nextBonusCents?: number | null;
  /** 0 = $50 (capped), 1 = $35, 2 = standing $25. */
  phase?: number;
  /** Existing yearly members could claim the thank-you during the October sale only. */
  courtesyOpen?: boolean;
  priceCents: number;
  bonusCents: number;
  bonusMonths: number;
  kind: 'signed_out' | 'new' | 'monthly' | 'yearly' | 'charter' | 'team' | 'none';
  bonusClaimed: boolean;
  bonusExpiresAt: string | null;
  /** A co-branded partner (e.g. Limelite Casting) when the visitor came in on their link. */
  partner?: { code: string; name: string } | null;
  /** Capped at the first `cap` new annual members (Joe, Oct 10 2026). */
  soldOut?: boolean;
  cap?: number;
  remaining?: number;
};

// The sale's name and the offer page's lines. Every surface (offer page, course
// page, plans card) uses these, so the amount and the deadline change in one place.
export const OFFER_NAME = 'The Hey Joe October Credit Sale';
const dollars = (cents?: number | null) => `$${Math.round((cents ?? 2500) / 100)}`;
/** "$50", "$35" or "$25": today's credit. */
export const creditLabel = (o?: OctOffer | null) => dollars(o?.bonusCents);
export const offerPageHeadline = (o?: OctOffer | null) => `Join Hey Joe Plus for $99/year and get ${creditLabel(o)} in Hey Joe credit.`;
export const offerScarcityLine = (o?: OctOffer | null) => {
  if (!o || o.phase === 0) return `${creditLabel(o)} for the first ${o?.cap ?? 100} new annual members through Monday, October 19. After that, the credit is ${dollars(o?.nextBonusCents ?? 3500)}.`;
  if (o.phase === 1) return `${creditLabel(o)} through October 31. After that, the credit is ${dollars(o.nextBonusCents ?? 2500)}.`;
  return `Every new year of Hey Joe Plus comes with ${creditLabel(o)} in Hey Joe credit.`;
};
/** For places that only have room for the extra: the plans card, under the price. */
export const offerShortLine = (o?: OctOffer | null) => o && o.phase === 2
  ? `Plus ${creditLabel(o)} in Hey Joe credit when you join.`
  : `Plus ${creditLabel(o)} in Hey Joe credit, ${o?.phase === 1 ? 'through October 31' : 'through October 19'}.`;
export const offerFinePrint = (o?: OctOffer | null) => `Your ${creditLabel(o)} in Hey Joe credit is good for events, classes, store items and video token packs for 12 months. It doesn't apply to reader bookings.`;

export async function fetchOctOffer(): Promise<OctOffer | null> {
  try {
    const ref = getStoredAffiliateRef();
    const r = await fetch(`/api/offer/oct26${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`, { headers: getAuthHeaders() });
    return r.ok ? ((await r.json()) as OctOffer) : null;
  } catch { return null; }
}

/** Shared by every surface; one request per page view is plenty. */
export function useOctOffer(): OctOffer | null {
  const [offer, setOffer] = useState<OctOffer | null>(null);
  useEffect(() => { let live = true; fetchOctOffer().then(o => { if (live) setOffer(o); }); return () => { live = false; }; }, []);
  return offer;
}

/** People the offer is pitched to: no paid Plus yet (or not signed in), or monthly. */
export const offerPitchable = (o: OctOffer | null) => !!o?.open && (o.kind === 'new' || o.kind === 'signed_out' || o.kind === 'monthly');

async function post(path: string, body?: object) {
  const r = await fetch(path, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body || {}) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(j.error || 'Something went wrong'), { code: j.code, manageUrl: j.manageUrl });
  return j;
}

export async function startOfferCheckout(returnTo?: string, campaign?: string, campaignProductId?: string): Promise<string> {
  // A join-from-an-event link keeps its own ?c / ?p so the creator is still credited.
  const ref = getStoredAffiliateRef();
  const j = await post('/api/subscription/create', { tier: 'yearly', offer: 'oct26', campaign: campaign || 'oct26', ...(campaignProductId ? { campaignProductId } : {}), ...(returnTo ? { returnTo } : {}), ...(ref ? { ref } : {}) });
  return j.checkoutUrl as string;
}
// Signed out: checkout on the account behind this email, no sign-in first.
// { checkoutUrl } to go to Stripe, or { needsAccount } when the email has no
// Hey Joe account yet. Throws with code SIGN_IN_FIRST when it already has Plus.
export async function startGuestOfferCheckout(email: string, campaign?: string, campaignProductId?: string): Promise<{ checkoutUrl?: string; needsAccount?: boolean }> {
  const ref = getStoredAffiliateRef();
  return post('/api/offer/oct26/guest-checkout', { email, campaign: campaign || 'oct26', ...(campaignProductId ? { campaignProductId } : {}), ...(ref ? { ref } : {}) });
}
export const switchToYearly = () => post('/api/offer/oct26/switch');
export const claimYearlyBonus = () => post('/api/offer/oct26/claim');
